import { useEffect, useMemo, useRef, useState } from "react";
import type { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import {
    CoaTemplateActivityEventRef,
    CoaTemplateActivityEventResult,
    getInitialSelectedEventIds,
    isCheckboxCapableEvent,
} from "./coaTemplateActivityEvents";

export function useCoaTemplateActivitySelection({
    eventRefs,
    events,
    isReadOnly,
    workspaceRolesById,
}: {
    eventRefs: CoaTemplateActivityEventRef[];
    events: CoaTemplateActivityEventResult[] | undefined;
    isReadOnly: boolean;
    workspaceRolesById: ReadonlyMap<string, UserWorkspaceRole>;
}) {
    const [activeEventId, setActiveEventId] = useState<string | null>(
        eventRefs[0]?.eventId ?? null,
    );
    const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(
        new Set(),
    );

    // Seed the default selection once per distinct set of loaded events. Keying
    // on the stable event ids (rather than object identity) prevents a
    // background react-query refetch or a fresh `workspaceRolesById` reference
    // from silently clobbering the user's toggled selection.
    const settledSelectionKeyRef = useRef<string | null>(null);
    useEffect(() => {
        if (!events) {
            return;
        }
        // The undo default depends on workspace roles, so wait for them to load
        // before settling; otherwise we'd lock in an empty default.
        if (!isReadOnly && workspaceRolesById.size === 0) {
            return;
        }
        const settledSelectionKey = events
            .map((event) => event.eventRef.eventId)
            .sort((a, b) => a.localeCompare(b))
            .join("|");
        if (settledSelectionKeyRef.current === settledSelectionKey) {
            return;
        }
        settledSelectionKeyRef.current = settledSelectionKey;
        setSelectedEventIds(
            getInitialSelectedEventIds({
                events,
                isReadOnly,
                workspaceRolesById,
            }),
        );
    }, [events, isReadOnly, workspaceRolesById]);

    useEffect(() => {
        if (
            eventRefs.length > 0 &&
            !eventRefs.some((eventRef) => eventRef.eventId === activeEventId)
        ) {
            setActiveEventId(eventRefs[0].eventId);
        }
    }, [activeEventId, eventRefs]);

    const selectableEventIds = useMemo(
        () =>
            new Set(
                (events ?? [])
                    .filter((event) =>
                        isCheckboxCapableEvent({
                            event,
                            workspaceRolesById,
                        }),
                    )
                    .map((event) => event.eventRef.eventId),
            ),
        [events, workspaceRolesById],
    );
    // Re-validate the seeded selection against current eligibility on every
    // render: a refetch can return the same event ids with changed eligibility
    // (e.g. an event becomes superseded or gets a revertedAt), so an id that is
    // no longer selectable must drop out of the derived selection. This never
    // mutates the settled default-seed state, so the user's still-valid picks
    // are preserved.
    const selectedEntityIds = useMemo(
        () =>
            new Set(
                eventRefs
                    .filter(
                        (eventRef) =>
                            selectedEventIds.has(eventRef.eventId) &&
                            selectableEventIds.has(eventRef.eventId),
                    )
                    .map((eventRef) => eventRef.entityId),
            ),
        [eventRefs, selectedEventIds, selectableEventIds],
    );
    const disabledEntityIds = useMemo(
        () =>
            new Set(
                eventRefs
                    .filter(
                        (eventRef) =>
                            isReadOnly ||
                            !selectableEventIds.has(eventRef.eventId),
                    )
                    .map((eventRef) => eventRef.entityId),
            ),
        [eventRefs, isReadOnly, selectableEventIds],
    );
    const activeEventRef =
        eventRefs.find((eventRef) => eventRef.eventId === activeEventId) ??
        null;
    const selectedEventRefs = eventRefs.filter(
        (eventRef) =>
            selectedEventIds.has(eventRef.eventId) &&
            selectableEventIds.has(eventRef.eventId),
    );

    return {
        activeEventRef,
        selectedEventRefs,
        selectedEntityIds,
        disabledEntityIds,
        setActiveEntityId: (entityId: number | null) => {
            const eventRef = eventRefs.find(
                (candidate) => candidate.entityId === entityId,
            );
            setActiveEventId(eventRef?.eventId ?? null);
        },
        setSelectedEntityIds: (entityIds: ReadonlySet<number>) => {
            setSelectedEventIds(
                new Set(
                    eventRefs
                        .filter(
                            (eventRef) =>
                                entityIds.has(eventRef.entityId) &&
                                selectableEventIds.has(eventRef.eventId),
                        )
                        .map((eventRef) => eventRef.eventId),
                ),
            );
        },
    };
}
